import { t, tl } from '../../i18n/t';
import { monthName, plural } from '../../lib/format';
import { letterPath } from '../briefe/labels';
import { jaPath } from '../jahresabschluss/labels';
import { rePath } from '../rechnungen/labels';
import type { Category, MonthState, NotificationItem } from './types';

// Ordner eines Monats, wie in der Demo (deutsche Texte, übersetzt wird beim Anzeigen: t(f.label))
export const FOLDERS: { key: Category; label: string; ico: string; sub: string }[] = [
  { key: 'ausgang', label: tl('Ausgangsbelege'), ico: 'A', sub: tl('Rechnungen an Ihre Kunden') },
  { key: 'eingang', label: tl('Eingangsbelege'), ico: 'E', sub: tl('Rechnungen und Quittungen, die Sie bezahlen') },
  { key: 'kontoauszug', label: tl('Kontoauszug'), ico: 'K', sub: tl('Bank, Kreditkarte') },
];

export const CATEGORY_LABEL: Record<Category, string> = {
  ausgang: tl('Ausgangsbelege'),
  eingang: tl('Eingangsbelege'),
  kontoauszug: tl('Kontoauszug'),
};

export const isCategory = (v: string | undefined): v is Category => v === 'ausgang' || v === 'eingang' || v === 'kontoauszug';

export const belegCount = (n: number) => plural(n, 'Beleg', 'Belege');

// Pfade der Ebenen: /app/belege/<Firma>/<Jahr>/<Monat>/<Ordner>
export function belegePath(clientId?: string, year?: number, month?: number, category?: Category): string {
  return ['/app/belege', clientId, year, month, category].filter((x) => x !== undefined).join('/');
}

// Meldungen zu einem Mitarbeiter (Angaben über den Link ausgefüllt, angemeldet, Änderung übernommen, Angaben von der
// Kanzlei geändert, abgemeldet, Kündigung korrigiert oder storniert): öffnen die Personalakte
export const EMPLOYEE_NOTES = new Set([
  'mitarbeiter_ausgefuellt',
  'mitarbeiter_angemeldet',
  'aenderung_uebernommen',
  'mitarbeiter_geaendert',
  'mitarbeiter_abgemeldet',
  'kuendigung_korrigiert',
  'kuendigung_storniert',
]);

// Meldungen zu einem Brief (Kommentar der Kanzlei, erledigt, von der Kanzlei eingescannt): öffnen den Brief
export const LETTER_NOTES = new Set(['brief_comment', 'brief_erledigt', 'brief_neu']);

// Meldungen zu einer angeforderten Unterlage (neue Datei, Kommentar, erledigt): öffnen die Anfrage auf der Übersicht
export const REQUEST_NOTES = new Set(['anfrage_datei', 'anfrage_comment', 'anfrage_erledigt']);

// Ziel eines Glocken-Eintrags (null: Beleg gibt es nicht mehr)
export function notificationTarget(n: NotificationItem): string | null {
  const l = n.link;
  if (!l) return null;
  if (LETTER_NOTES.has(n.type)) return l.letterId ? letterPath(n.clientId, l.letterId) : null;
  if (REQUEST_NOTES.has(n.type)) return l.requestId ? `/app?anfrage=${l.requestId}` : null;
  if (n.type === 'ustva_filed') return l.filingId ? `/app?ustva=${l.filingId}` : null;
  // von der Kanzlei vorbereitete Voranmeldung: steht als Karte auf der Übersicht
  if (n.type === 'ustva_pruefen') return '/app';
  if (n.type === 'bwa_uploaded') return l.bwaId ? `/app?bwa=${l.bwaId}` : null;
  if (n.type === 'kanzlei_dokument' || n.type === 'unterschrift') return l.docId ? `/app?dok=${l.docId}` : null;
  if (n.type === 'jahresabschluss') return jaPath(n.clientId);
  if (n.type.startsWith('rechnung')) return l.invoiceId ? `${rePath(n.clientId)}?rechnung=${l.invoiceId}` : null;
  // Kunde hat ein Angebot über den Link angenommen oder abgelehnt: die Angebote im Rechnungswerkzeug, mit der Firma
  if (n.type.startsWith('angebot_')) return l.angebotId ? `/rechnung/angebote?firma=${n.clientId}&angebot=${l.angebotId}` : null;
  if (EMPLOYEE_NOTES.has(n.type)) return l.employeeId ? `/app/mitarbeiter/${n.clientId}?ma=${l.employeeId}` : null;
  if (!l.year || !l.month) return null;
  if (n.type === 'lohn_unterlagen') return `/app/mitarbeiter/${n.clientId}/lohn/${l.year}/${l.month}`;
  const base = belegePath(n.clientId, l.year, l.month, l.category);
  return l.belegId ? `${base}?beleg=${l.belegId}` : base;
}

// Abzeichen im Monatsraster (A4), in der Sprache der App
export function monthBadge(state: MonthState, count: number): { label: string; cls: string } {
  switch (state) {
    case 'gebucht':
      return { label: t('gebucht ✓'), cls: 'b-ok' };
    case 'nachtrag':
      return { label: t('Nachtrag'), cls: 'b-none' };
    case 'fehlt':
      return { label: t('Beleg fehlt'), cls: 'b-warn' };
    case 'nachgereicht':
      return { label: t('nachgereicht'), cls: 'b-none' };
    case 'hochgeladen':
      return { label: t('{n} hochgeladen', { n: count }), cls: 'b-none' };
    case 'offen':
      return { label: t('offen'), cls: 'b-none' };
    case 'laeuft':
      return { label: t('läuft'), cls: 'b-none' };
    default:
      return { label: '–', cls: 'b-none' };
  }
}

export const badgeClass = (tone: 'ok' | 'warn' | 'none') => `badge b-${tone}`;

// „Juli, August und September“
export function monthList(ms: { month: number }[]): string {
  const names = ms.map((m) => monthName(m.month));
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} ${t('und')} ${names[names.length - 1]}` : (names[0] ?? '');
}

// Gleiche Namen unterscheidbar machen (neueste zuerst): image.jpg, image (2).jpg
export function displayNames(names: string[]): string[] {
  return names.map((n, i) => {
    const k = names.slice(i).filter((x) => x === n).length;
    if (k < 2) return n;
    const dot = n.lastIndexOf('.');
    return dot > 0 ? `${n.slice(0, dot)} (${k})${n.slice(dot)}` : `${n} (${k})`;
  });
}

// Hinweis der Kanzlei ohne Schlusspunkt, damit der Satz danach weitergeht
export const noteText = (note: string | null) => (note ?? '').trim().replace(/[.!]+$/, '');
